import React, { useState, useCallback, useEffect, useRef } from "react";
import { useMsal, useIsAuthenticated, AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";

import AppHeader from "./components/layout/AppHeader";
import DashboardPage from "./pages/DashboardPage";
import SelectTypePage from "./pages/SelectTypePage";
import ToolNamePage from "./pages/ToolNamePage";
import AssessmentPage from "./pages/AssessmentPage";
import SubmittedPage from "./pages/SubmittedPage";
import SigninWidget from "./auth/SigninWidget";
import { useAssessment } from "./hooks/useAssessment";
import { useDashboard } from "./hooks/useDashboard";

const PAGES = {
  DASHBOARD:  "dashboard",
  SELECT:     "select",
  TOOL_NAME:  "toolname",
  ASSESSMENT: "assessment",
  SUBMITTED:  "submitted",
};

const App = () => {
  const [currentPage, setCurrentPage] = useState(PAGES.DASHBOARD);
  const [showDraftToast, setShowDraftToast]   = useState(false);
  const [showErrorToast, setShowErrorToast]   = useState(false); // ✅ NEW
  const [submittedData, setSubmittedData]     = useState(null);
  const [isSubmitting, setIsSubmitting]       = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const { accounts, instance } = useMsal();

  const assessmentRef = useRef(null);

  useEffect(() => {
    const handleRedirectResponse = async () => {
      try {
        const response = await instance.handleRedirectPromise();
        if (response) {
          const userEmail = response.account?.username;
          const userName  = response.account?.name;
          localStorage.setItem("userinfo",               userEmail?.toLowerCase() || "");
          localStorage.setItem("userName",               userName || "");
          localStorage.setItem("access-token-storage",  JSON.stringify({ accessToken: response.idToken }));
        }
      } catch (error) {
        console.warn("No redirect response to handle:", error);
      }
    };
    handleRedirectResponse();
  }, [instance]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'msal.account.keys' || event.key === 'msal.token.keys') {
        try { instance.getActiveAccount(); } catch (err) { console.warn(err); }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [instance]);

  const dashboard = useDashboard();

  const handleSave = useCallback(
    (data) => {
      dashboard.upsertRecord(data);
      if (data.status === "Submitted") {
        setSubmittedData({
          apiSections: assessmentRef.current?.apiSections || [],
          answers:     assessmentRef.current?.answers     || {},
        });
        setIsSubmitting(false);
        setCurrentPage(PAGES.SUBMITTED);
      }
    },
    [dashboard]
  );

  const assessment = useAssessment(handleSave);
  assessmentRef.current = assessment;

  const goNewForm = useCallback(() => setCurrentPage(PAGES.SELECT), []);

  const handleTypeSelect = useCallback((type) => {
    assessment.setSelectedType(type);
    setCurrentPage(PAGES.TOOL_NAME);
  }, [assessment]);

  const handleToolSubmit = useCallback((toolName) => {
    assessment.startNew(assessment.selectedType, toolName);
    setCurrentPage(PAGES.ASSESSMENT);
  }, [assessment]);

  const handleEdit = useCallback(async (record) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/form/${record.id}`);
      if (!res.ok) throw new Error(`Failed to load form: ${res.status}`);
      const data = await res.json();

      const rawSections = (data.structure?.sections || []).sort((a, b) => a.order - b.order);

      const apiSections = rawSections.map((sec) => ({
        section:   sec.section,
        order:     sec.order,
        questions: sec.questions.map((q) => ({
          id:           q.id,
          question:     q.question,
          description:  q.description  || "",
          subDimension: q.subDimension || "",
          response:     q.response     || null,
          comment:      q.comment      || "",
        })),
      }));

      const answers = Object.fromEntries(
        rawSections.map((sec) => [
          sec.section,
          sec.questions.map((q) => ({
            id:       q.id,
            response: q.response || null,
            comment:  q.comment  || "",
          })),
        ])
      );

      assessment.loadRecord({
        id:       data.form_id,
        type:     data.structure?.tool_type,
        toolName: data.structure?.tool,
        apiSections,
        answers,
      });

      setCurrentPage(PAGES.ASSESSMENT);
    } catch (err) {
      console.error("Failed to load form for editing:", err);
      alert("Could not load form. Please try again.");
    }
  }, [assessment]);

  // ✅ Show success toast instantly — API saves in background
  // If API fails, hide success toast and show error toast
  const handleSaveAsDraft = useCallback(async () => {
    // Show success toast immediately — don't wait for API
    setShowErrorToast(false);
    setShowDraftToast(true);
    setTimeout(() => setShowDraftToast(false), 3500);

    try {
      await assessment.save("Draft");
    } catch (err) {
      // API failed — replace success toast with error toast
      setShowDraftToast(false);
      setShowErrorToast(true);
      setTimeout(() => setShowErrorToast(false), 4000);
    }
  }, [assessment]);

  const handleSubmit = useCallback(() => {
    setIsSubmitting(true);
    assessment.save("Submitted");
  }, [assessment]);

  return (
    <div style={{ minHeight: "100vh", fontFamily: "'Segoe UI', Arial, sans-serif" }}>
      <AuthenticatedTemplate>
        <div>
          <AppHeader />

          {currentPage === PAGES.DASHBOARD && (
            <DashboardPage
              counts={dashboard.counts}
              filteredRecords={dashboard.filteredRecords}
              activeFilter={dashboard.activeFilter}
              searchQuery={dashboard.searchQuery}
              typeFilter={dashboard.typeFilter}
              setActiveFilter={dashboard.setActiveFilter}
              setSearchQuery={dashboard.setSearchQuery}
              setTypeFilter={dashboard.setTypeFilter}
              onNewForm={goNewForm}
              onEdit={handleEdit}
              loadingRecords={dashboard.loadingRecords}
            />
          )}

          {currentPage === PAGES.SELECT && (
            <SelectTypePage onSelect={handleTypeSelect} />
          )}

          {currentPage === PAGES.TOOL_NAME && (
            <ToolNamePage onSubmit={handleToolSubmit} />
          )}

          {currentPage === PAGES.ASSESSMENT && (
            <AssessmentPage
              selectedType={assessment.selectedType}
              toolName={assessment.toolName}
              sections={assessment.sections}
              reportIdx={assessment.reportIdx}
              apiSections={assessment.apiSections}
              loading={assessment.loading}
              error={assessment.error}
              currentSectionIdx={assessment.currentSectionIdx}
              answers={assessment.answers}
              completion={assessment.completion}
              reportUnlocked={assessment.reportUnlocked}
              updateAnswer={assessment.updateAnswer}
              goToSection={assessment.goToSection}
              goNext={assessment.goNext}
              goPrev={assessment.goPrev}
              onBack={() => setCurrentPage(PAGES.DASHBOARD)}
              onSaveAsDraft={handleSaveAsDraft}
              onSubmit={handleSubmit}
              showDraftToast={showDraftToast}
              showErrorToast={showErrorToast}  // ✅ NEW
              isSubmitting={isSubmitting}
            />
          )}

          {currentPage === PAGES.SUBMITTED && submittedData && (
            <SubmittedPage
              apiSections={submittedData.apiSections}
              answers={submittedData.answers}
              onGoToDashboard={() => setCurrentPage(PAGES.DASHBOARD)}
            />
          )}
        </div>
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <SigninWidget />
      </UnauthenticatedTemplate>
    </div>
  );
};

export default App;